import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { Gift, Copy, WhatsappLogo, Users, Coins } from "@phosphor-icons/react";

export function ReferEarn({ compact = false }) {
  const { data } = useQuery({ queryKey: ["referrals-me"], queryFn: async () => (await api.get("/referrals/me")).data });
  if (!data || !data.enabled) return null;
  const bonus = money(data.bonus_cents, "USD");
  const msg = `I'm using iYeneh.com to send data, airtime, top-up credit, call and text worldwide. Sign up with my link, you and I earn ${bonus} credit each when you make your first top-up: ${data.link}`;
  const wa = `https://wa.me/?text=${encodeURIComponent(msg)}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.link);
      toast.success("Invite link copied");
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = data.link; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
        toast.success("Invite link copied");
      } catch { toast.error("Couldn't copy — long-press the link to copy it."); }
    }
  };

  if (compact) {
    return (
      <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4" data-testid="refer-earn-compact">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground"><Gift size={20} weight="fill" /></div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold">Earn {bonus} per friend who tops up</p>
            <p className="truncate text-xs text-muted-foreground">Share your link on WhatsApp — you earn when they make their first top-up.</p>
          </div>
          <a data-testid="refer-wa-compact" href={wa} target="_blank" rel="noreferrer"
            className="flex items-center gap-1.5 rounded-full bg-[#25D366] px-4 py-2 text-sm font-semibold text-white"><WhatsappLogo size={16} weight="fill" /> Share</a>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-6" data-testid="refer-earn">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground"><Gift size={22} weight="fill" /></div>
        <div>
          <h2 className="font-display text-lg font-semibold">Refer &amp; Earn</h2>
          <p className="text-xs text-muted-foreground">You earn <span className="font-semibold text-foreground">{bonus}</span> credit when a friend signs up and makes their first top-up.</p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-secondary p-3 text-center">
          <p className="flex items-center justify-center gap-1 text-xs text-muted-foreground"><Users size={13} /> Friends joined</p>
          <p data-testid="refer-count" className="mt-1 font-mono-metric text-2xl font-semibold">{data.count}</p>
        </div>
        <div className="rounded-xl bg-secondary p-3 text-center">
          <p className="flex items-center justify-center gap-1 text-xs text-muted-foreground"><Coins size={13} /> Earned</p>
          <p data-testid="refer-earned" className="mt-1 font-mono-metric text-2xl font-semibold text-emerald-600">{money(data.earned_cents, "USD")}</p>
        </div>
      </div>

      <label className="mt-4 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Your invite link</label>
      <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-input bg-background px-3 py-2.5">
        <span data-testid="refer-link" className="min-w-0 flex-1 truncate text-sm">{data.link}</span>
        <button data-testid="refer-copy" onClick={copy} className="rounded-lg p-1.5 text-muted-foreground hover:text-primary"><Copy size={16} /></button>
      </div>

      <a data-testid="refer-wa" href={wa} target="_blank" rel="noreferrer"
        className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] py-3 font-semibold text-white transition-transform hover:scale-[1.02]">
        <WhatsappLogo size={20} weight="fill" /> Share on WhatsApp
      </a>
      <p className="mt-2 text-center text-[11px] text-muted-foreground">Code <span className="font-mono-metric font-semibold">{data.code}</span></p>
    </div>
  );
}
